import React, { useCallback } from 'react';

import ClinicalNotePainSection from './ClinicalNotePainSection.jsx';
import ClinicalNoteRomSection from './ClinicalNoteRomSection.jsx';
import ClinicalNoteStrengthSection from './ClinicalNoteStrengthSection.jsx';
import ClinicalNoteGoalSection from './ClinicalNoteGoalSection.jsx';
import ClinicalNoteSpecialTestSection from './ClinicalNoteSpecialTestSection.jsx';
import ClinicalNotePalpationSection from './ClinicalNotePalpationSection.jsx';
import ClinicalNoteContentSection from './ClinicalNoteContentSection.jsx';
import ClinicalNoteTreatmentSection from './ClinicalNoteTreatmentSection.jsx';
import ClinicalNoteAttachedDailySection from './ClinicalNoteAttachedDailySection.jsx';
import ClinicalNoteCollapsibleSection from './ClinicalNoteCollapsibleSection.jsx';
import ClinicalNotePlanOfCareGate from './ClinicalNotePlanOfCareGate.jsx';
import useClinicalSectionStatuses from './useClinicalSectionStatuses.js';

/**
 * Progress-note workspace, including the same-visit Attached Daily Note.
 *
 * The first clinical section opens on entry; the other sections stay
 * collapsed until selected. Collapsed sections remain mounted, allowing
 * their autosave and navigation-save registrations to remain active.
 *
 * Progress Notes include the treatment narrative and a conditional
 * Plan of Care gate. The attached daily entry belongs to this same visit.
 */
export default function ClinicalNoteProgressForm({
  clinicalNoteId,
  disabled = false,
  onSaved,
  onVersionChange,
  navigationCoordinator = null,
}) {
  const resetKey = String(clinicalNoteId ?? 'new');
  const accordionGroup = `clinical-note-${resetKey}`;
  const { statuses, refreshSectionStatuses } =
    useClinicalSectionStatuses(clinicalNoteId);

  const handleSaved = useCallback(
    async (...args) => {
      if (typeof onSaved === 'function') {
        onSaved(...args);
      }
      await refreshSectionStatuses();
    },
    [onSaved, refreshSectionStatuses]
  );

  const commonProps = {
    clinicalNoteId,
    disabled,
    onSaved: handleSaved,
    onVersionChange,
    navigationCoordinator,
  };

  // Keep this list synchronized with rendered accordion sections and
  // the Plan of Care gate's previous/next section keys.
  const sections = [
    'pain',
    'rom',
    'strength',
    'goals',
    'special-tests',
    'palpation',
    'content',
    'treatment',
    'plan-of-care',
    'attached-daily',
  ];

  const accordionProps = (key, index) => ({
    sectionKey: `${resetKey}-${key}`,
    accordionGroup,
    previousSectionKey:
      index > 0 ? `${resetKey}-${sections[index - 1]}` : null,
    nextSectionKey:
      index < sections.length - 1
        ? `${resetKey}-${sections[index + 1]}`
        : null,
  });

  return (
    <div className="clinical-note-section-stack">
      <ClinicalNoteCollapsibleSection
        title="Pain"
        defaultCollapsed={false}
        status={statuses.PAIN}
        resetKey={`${resetKey}-pain`}
        {...accordionProps('pain', 0)}
      >
        <ClinicalNotePainSection {...commonProps} />
      </ClinicalNoteCollapsibleSection>

      <ClinicalNoteCollapsibleSection
        title="Range of Motion"
        defaultCollapsed
        status={statuses.ROM}
        resetKey={`${resetKey}-rom`}
        {...accordionProps('rom', 1)}
      >
        <ClinicalNoteRomSection {...commonProps} />
      </ClinicalNoteCollapsibleSection>

      <ClinicalNoteCollapsibleSection
        title="Strength / GMMT"
        defaultCollapsed
        status={statuses.STRENGTH}
        resetKey={`${resetKey}-strength`}
        {...accordionProps('strength', 2)}
      >
        <ClinicalNoteStrengthSection {...commonProps} />
      </ClinicalNoteCollapsibleSection>

      <ClinicalNoteCollapsibleSection
        title="Goals"
        defaultCollapsed
        status={statuses.GOAL}
        resetKey={`${resetKey}-goals`}
        {...accordionProps('goals', 3)}
      >
        <ClinicalNoteGoalSection {...commonProps} />
      </ClinicalNoteCollapsibleSection>

      <ClinicalNoteCollapsibleSection
        title="Special Tests"
        defaultCollapsed
        status={statuses.SPECIAL_TEST}
        resetKey={`${resetKey}-special-tests`}
        {...accordionProps('special-tests', 4)}
      >
        <ClinicalNoteSpecialTestSection {...commonProps} />
      </ClinicalNoteCollapsibleSection>

      <ClinicalNoteCollapsibleSection
        title="Palpation"
        defaultCollapsed
        status={statuses.PALPATION}
        resetKey={`${resetKey}-palpation`}
        {...accordionProps('palpation', 5)}
      >
        <ClinicalNotePalpationSection {...commonProps} />
      </ClinicalNoteCollapsibleSection>

      <ClinicalNoteCollapsibleSection
        title="Clinical Content / Narrative"
        defaultCollapsed
        status={statuses.CONTENT}
        resetKey={`${resetKey}-content`}
        {...accordionProps('content', 6)}
      >
        <ClinicalNoteContentSection {...commonProps} />
      </ClinicalNoteCollapsibleSection>

      <ClinicalNoteCollapsibleSection
        title="Treatment"
        defaultCollapsed
        status={statuses.TREATMENT}
        resetKey={`${resetKey}-treatment`}
        {...accordionProps('treatment', 7)}
      >
        <ClinicalNoteTreatmentSection {...commonProps} />
      </ClinicalNoteCollapsibleSection>

      <ClinicalNotePlanOfCareGate
        {...commonProps}
        status={statuses.PLAN_OF_CARE}
        accordionGroup={accordionGroup}
        sectionKey={`${resetKey}-plan-of-care`}
        previousSectionKey={`${resetKey}-treatment`}
        nextSectionKey={`${resetKey}-attached-daily`}
      />

      <ClinicalNoteCollapsibleSection
        title="Attached Daily Note — Same Visit"
        defaultCollapsed
        status={statuses.ATTACHED_DAILY}
        resetKey={`${resetKey}-attached-daily`}
        {...accordionProps('attached-daily', 9)}
      >
        <ClinicalNoteAttachedDailySection {...commonProps} />
      </ClinicalNoteCollapsibleSection>
    </div>
  );
}
